/**
 * "This row | the TOTAL" segmented toggle — what the comparison reads from.
 * A two-state choice the client must NOTICE reads better as side-by-side
 * pills than as a dropdown that hides the alternative. Drop-scoped RHS
 * fields (source_* / list_*) have no meaningful population TOTAL, so the
 * toggle collapses to a single static "This row" pill there.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC } from 'react';

export interface AgainstToggleProps {
  total: boolean;
  onChange: (total: boolean) => void;
  /** No population TOTAL for this RHS (source_* / list_*): self only. */
  totalUnavailable?: boolean;
  size?: 'sm' | 'md';
  labels?: { self: string; total: string };
}

const TITLE =
  'This row = the element’s own numbers. The TOTAL = the blended total of the whole table (its TOTAL row) over the chosen window.';

export const AgainstToggle: FC<AgainstToggleProps> = ({
  total,
  onChange,
  totalUnavailable,
  size = 'md',
  labels = { self: 'This row', total: 'the TOTAL' },
}) => {
  const h = size === 'sm' ? 'h-8' : 'h-9';
  const pill = (active: boolean): string =>
    `${h} px-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      active
        ? 'bg-sky-600 text-white'
        : 'bg-background text-muted-foreground hover:text-foreground'
    }`;
  if (totalUnavailable) {
    return (
      <span
        className={`inline-flex ${h} items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground`}
        title="Source/list context is per drop — there is no table TOTAL to compare against."
      >
        {labels.self}
      </span>
    );
  }
  return (
    <div
      role="group"
      aria-label="Compare against"
      title={TITLE}
      className="inline-flex shrink-0 overflow-hidden rounded-md border border-input"
    >
      <button
        type="button"
        aria-pressed={!total}
        onClick={() => onChange(false)}
        className={pill(!total)}
      >
        {labels.self}
      </button>
      <button
        type="button"
        aria-pressed={total}
        onClick={() => onChange(true)}
        className={`${pill(total)} border-l border-input`}
      >
        {labels.total}
      </button>
    </div>
  );
};

export default AgainstToggle;
